import { useLoaderData, useRouteLoaderData } from 'react-router';

import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import * as Sentry from '@sentry/remix';
import type {
  RSSFeedDataPodcast,
  RSSFeedDataPodcastAppleEpisode,
  RSSFeedDataPodcastItem,
  RSSFeedDataPodcastSpotifyEpisode,
  RSSFeedDataYouTubeEntry,
} from '@shared/dream-components';
import { getEpisodeSlug, isYouTubeShort, normalizeToArray, PodcastRssFeedEpisode } from '@shared/dream-components';

import { useMetaViewContent } from '~/components/Pixels/useMetaViewContent';
import StaticPage from '~/components/StaticPage';
import type { Page } from '~/interfaces/page';
import type { PodcastEpisode } from '~/interfaces/podcast';
import type { Site } from '~/interfaces/site';
import type { RootLoaderData } from '~/root';
import getPage from '~/services/api/pages/getPage.server';
import getRssApplePodcast from '~/services/api/rss_apple_podcast/getRssApplePodcast.server';
import getRssFeed from '~/services/api/rss_feed/getRssFeed.server';
import getRssSpotify from '~/services/api/rss_spotify_podcast/getRssSpotify';
import { getRenderableFontsCss } from '~/services/fonts';
import type { GoogleFontEntryType } from '~/services/fonts/cache';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import { publicationFromMatches, siteFromMatches } from '~/utils/publicationFromLoaderData';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import { mapRssFeedDataToPodcastEpisode } from './mapRssFeedDataToPodcastEpisode';
import { findBestMatchingEntry } from './util';

export type StaticRSSFeedPageLoaderData = {
  rssFeedData: RSSFeedDataPodcast | undefined;
  rssFeedURL: string;
  rssFeedItunesID: string;
  rssFeedItemGUID: string;
  currentEpisodeApplePodcast: RSSFeedDataPodcastAppleEpisode | undefined;
  currentEpisodeSpotifyPodcast: RSSFeedDataPodcastSpotifyEpisode | undefined;
  currentEpisode: RSSFeedDataPodcastItem | undefined;
  podcastEpisode: PodcastEpisode | undefined;
  page: Page | undefined;
  renderableFonts: string[];
  renderableFontsCss: GoogleFontEntryType[];
  adobeFontUrls: string[];
  requestUrl: string;
  currentEpisodeYouTube: RSSFeedDataYouTubeEntry | undefined;
  feedId: string;
};

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const currentEpisode = data?.currentEpisode;
  const publication = publicationFromMatches(matches);
  const requestUrl = data?.requestUrl || '';
  const schemaUrl = getCanonicalUrl(requestUrl);

  const imageUrl = currentEpisode?.['itunes:image']?.['@href'] || '';

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(requestUrl),
    matches,
    returnEarlyCheck: !currentEpisode,
    pageMetadata: data?.page?.viewable_page_version?.metadata as Record<string, unknown> | undefined,
    siteMetadata: siteFromMatches(matches)?.viewable_site_version?.metadata as Record<string, unknown> | undefined,
    overrides: {
      title: currentEpisode?.title || '',
      description: currentEpisode?.description || '',
      'og:url': requestUrl,
      'og:title': currentEpisode?.title || '',
      'og:description': currentEpisode?.description || '',
      'og:site_name': publication?.name || '',
      'og:image': imageUrl || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:image:alt': currentEpisode?.title || publication?.description || '',
      'twitter:url': requestUrl,
      'twitter:title': currentEpisode?.title || '',
      'twitter:description': currentEpisode?.meta_twitter_description || '',
      'twitter:image': imageUrl || publication?.thumbnail?.url || publication?.logo?.url || '',
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        identifier: currentEpisode?.guid || '',
        url: schemaUrl,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': schemaUrl,
        },
        headline: currentEpisode?.title || '',
        description: currentEpisode?.description || '',
        datePublished: currentEpisode?.pubDate || '',
        image: imageUrl
          ? {
              '@type': 'ImageObject',
              url: imageUrl,
            }
          : undefined,
        publisher: publication
          ? {
              '@type': 'Organization',
              name: publication?.name || '',
              url: publication?.url || '',
              identifier: publication?.id || '',
              logo: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
              image: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
            }
          : undefined,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url || '',
              name: publication?.name || '',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': schemaUrl,
              name: currentEpisode?.title || '',
            },
          },
        ],
      },
    ],
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);

  const site = await context.getSite(request, session);

  let page: Page | undefined;
  try {
    page = await getPage(request, session, {
      pathname: 'podcast/s/$showSlug/$episodeSlug',
    });
  } catch {
    // Page may not exist for this pathname
    // Render the static page
  }

  const rssFeedMap = (site as Site)?.viewable_site_version?.customization?.rss_feed_map;

  const rssFeedItem = Object.entries(rssFeedMap ?? {}).find(([_, feed]) => feed.uuid === params.feedId);
  const rssFeedURL = rssFeedItem?.[0];
  const rssFeedItunesID = rssFeedItem?.[1].itunes_id;
  const rssFeedSpotifyID = rssFeedItem?.[1].spotify_id;
  const rssYouTubeURL = rssFeedItem?.[1].youtube_url;
  const rssFeedData = rssFeedURL ? await getRssFeed(request, session, rssFeedURL) : undefined;

  const feedItems = normalizeToArray(rssFeedData?.rss?.channel?.item);
  const currentEpisode = feedItems.find(
    (item: RSSFeedDataPodcastItem) => getEpisodeSlug(item.title) === params.episodeSlug,
  );

  const itunesFeedURL = `https://itunes.apple.com/lookup?id=${rssFeedItunesID}&entity=podcastEpisode&limit=200`;

  const settledValue = <T,>(result: PromiseSettledResult<T>): T | undefined => {
    if (result.status === 'fulfilled') {
      return result.value;
    }

    Sentry.captureException(result.reason);
    return undefined;
  };

  const [appleResult, spotifyResult, youtubeResult, fontsResult] = await Promise.allSettled([
    rssFeedItunesID ? getRssApplePodcast(request, session, itunesFeedURL) : undefined,
    rssFeedSpotifyID
      ? getRssSpotify(request, session, rssFeedSpotifyID, currentEpisode?.guid, currentEpisode?.title || '')
      : undefined,
    rssYouTubeURL ? getRssFeed(request, session, rssYouTubeURL) : undefined,
    getRenderableFontsCss(site as Site, page),
  ]);

  const rssApplePodcastData = settledValue(appleResult);
  const rssSpotifyPodcastData = settledValue(spotifyResult);
  const youtubeRssFeedData = settledValue(youtubeResult);
  const fontsData = settledValue(fontsResult);

  const currentEpisodeApplePodcast = rssApplePodcastData?.results.find(
    (item: RSSFeedDataPodcastAppleEpisode) => item.episodeGuid === currentEpisode?.guid,
  );

  const youtubeEntries = youtubeRssFeedData?.feed?.entry;

  // Filter out YouTube Shorts so podcast episode matching only considers full videos
  const filteredYoutubeEntries = youtubeEntries?.filter((entry: RSSFeedDataYouTubeEntry) => !isYouTubeShort(entry));

  // Find best matching YouTube entry based on episode title, with date fallback
  const currentEpisodeYouTube =
    filteredYoutubeEntries && currentEpisode?.title
      ? findBestMatchingEntry<RSSFeedDataYouTubeEntry>(
          filteredYoutubeEntries,
          currentEpisode.title,
          currentEpisode.pubDate,
        )
      : undefined;

  let podcastEpisode: PodcastEpisode | undefined;
  try {
    if (currentEpisode && params.feedId && params.episodeSlug) {
      podcastEpisode = mapRssFeedDataToPodcastEpisode({
        rssItem: currentEpisode,
        rssFeedData,
        appleEpisode: currentEpisodeApplePodcast,
        spotifyEpisode: rssSpotifyPodcastData as RSSFeedDataPodcastSpotifyEpisode | undefined,
        youtubeEntry: currentEpisodeYouTube,
        feedId: params.feedId,
        episodeSlug: params.episodeSlug,
        requestUrl: url.href,
      });
    }
  } catch (error) {
    Sentry.captureException(error);
  }

  const renderableFontsCss = fontsData?.fontsCss ?? '';
  const adobeFontUrls = fontsData?.adobeFontUrls ?? [];

  return Response.json({
    rssFeedData,
    rssFeedURL,
    rssApplePodcastData,
    rssFeedItunesID,
    rssFeedItemGUID: currentEpisode?.guid,
    currentEpisodeApplePodcast,
    currentEpisodeSpotifyPodcast: rssSpotifyPodcastData,
    currentEpisode,
    podcastEpisode,
    page,
    renderableFontsCss,
    adobeFontUrls,
    requestUrl: url.href,
    currentEpisodeYouTube,
    feedId: params.feedId,
  });
};

// export const clientLoader = staticPageClientLoader;
const Index = () => {
  const data = useLoaderData() as StaticRSSFeedPageLoaderData;
  const {
    rssFeedData,
    rssFeedURL,
    currentEpisodeApplePodcast,
    currentEpisode,
    currentEpisodeSpotifyPodcast,
    currentEpisodeYouTube,
    page,
    podcastEpisode,
  } = data;
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const episodeId = podcastEpisode?.id || currentEpisode?.guid;
  useMetaViewContent(
    episodeId,
    episodeId
      ? {
          content_type: 'podcast_episode',
          content_ids: [episodeId],
        }
      : null,
  );

  // Render dynamic page if a page exists and rss/youtube data was
  // mapped correctly to podcast episode shape
  // When podcasts (beehiiv) feature is live, publishers will add
  // a dynamic page for the episode, and the rss/youtube should render
  // the page as designed by the publisher
  if (page && podcastEpisode) {
    return <StaticPage site={site} page={page} forceShowFooter forceShowNavbar isSearchModalEnabled />;
  }

  return (
    <StaticPage site={site} forceShowFooter forceShowNavbar isSearchModalEnabled>
      <div
        className="flex flex-col items-center w-full"
        style={{ backgroundColor: site.primary_theme.background_color }}
      >
        <PodcastRssFeedEpisode
          podcastEpisode={currentEpisode}
          applePodcastEpisode={currentEpisodeApplePodcast}
          spotifyPodcastEpisode={currentEpisodeSpotifyPodcast}
          youtubePodcastEpisode={currentEpisodeYouTube}
          theme={site.primary_theme}
          rssFeedData={rssFeedData}
          url={rssFeedURL}
          rssFeedMap={site.viewable_site_version?.customization?.rss_feed_map}
        />
      </div>
    </StaticPage>
  );
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Index;
